import { isHostnameMatched } from '../utils/tenantResolutionStore';
import { DEFAULT_TENANT_SLUG } from '../hooks/useTenant';

/**
 * Öffentliche Marketing-Routen, auf denen der CookieConsentBanner erscheinen
 * darf (Landing-Page, nicht sensible Auth-Seiten, Rechtstexte, MCP-Seite).
 *
 * Reset-, Verifizierungs- und OAuth-Callback-Routen sind bewusst nicht
 * enthalten, weil ihre URLs typischerweise Tokens oder Codes tragen.
 * /forgot-password ist oeffentlich, wird aber mangels SEO-Nutzen nicht
 * statisch vorgerendert und fehlt deshalb in scripts/prerender.js.
 */
export const PUBLIC_MARKETING_ROUTES = [
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/datenschutz',
  '/agb',
  '/mcp',
];

/**
 * Normalisiert einen Pfad: erzwingt Kleinschreibung und entfernt einen
 * abschließenden Slash (außer beim reinen Wurzelpfad "/").
 *
 * @param {string} pathname
 * @returns {string}
 */
const normalizePath = (pathname) => {
  const lower = (pathname || '/').toLowerCase();
  if (lower.length > 1 && lower.endsWith('/')) {
    return lower.slice(0, -1);
  }
  return lower;
};

/**
 * Entfernt im unmatched-Modus (Slug-Präfix-Routing, z.B. "/rentinger/agb")
 * das erste Pfadsegment (Tenant-Slug), sodass "/rentinger/agb" zu "/agb"
 * und "/rentinger" zu "/" wird. Im matched-Modus (Clean-URL-Hostname) wird
 * der Pfad unverändert zurückgegeben.
 *
 * @param {string} normalizedPath
 * @returns {string}
 */
const stripTenantSlug = (normalizedPath) => {
  if (isHostnameMatched()) {
    return normalizedPath;
  }
  const segments = normalizedPath.split('/').filter(Boolean);
  if (segments.length === 0) {
    return '/';
  }
  const rest = segments.slice(1).join('/');
  return rest ? `/${rest}` : '/';
};

/**
 * Prüft, ob der übergebene Pfad zu den öffentlichen Marketing-Routen gehört,
 * auf denen der Cookie-Consent-Banner angezeigt werden darf. Unbekannte
 * Routen liefern false.
 *
 * KRITISCH: Der CookieConsentBanner wird in App.js außerhalb von <Routes>
 * gemountet (nach <AppRoutes /> und <DeepLinkHandler />, analog zu
 * <InstallAppBanner />). Dort liefert useParams() ein leeres Objekt, weil
 * kein <Route>-Match aktiv ist — useTenantPath()/useResolvedTenantPath()
 * (useResolvedTenantSlug.js) würden im unmatched-Modus daher einen LEEREN
 * Slug liefern und fehlerhafte Pfade wie "//login" bauen. Diese Funktion
 * verwendet deshalb bewusst das hook-freie isHostnameMatched() aus
 * tenantResolutionStore.js, das unabhängig vom Router-Kontext funktioniert.
 *
 * @param {string} pathname - z.B. aus useLocation().pathname
 * @returns {boolean}
 */
export const isPublicMarketingPath = (pathname) => {
  const normalized = stripTenantSlug(normalizePath(pathname));
  return PUBLIC_MARKETING_ROUTES.includes(normalized);
};

/**
 * Baut einen Pfad zu einer öffentlichen Route, passend zum aktuellen
 * Routing-Modus (siehe Kommentar bei isPublicMarketingPath zur Begründung,
 * warum hier NICHT useResolvedTenantPath()/useTenantPath() genutzt wird).
 *
 * Im matched-Modus wird `suffix` unverändert zurückgegeben. Im
 * unmatched-Modus wird der Tenant-Slug aus dem aktuellen Pfad vorangestellt
 * (Fallback auf DEFAULT_TENANT_SLUG, falls der Pfad ausnahmsweise kein
 * Slug-Segment enthält).
 *
 * @param {string} pathname - aktueller Pfad (für die Slug-Ermittlung)
 * @param {string} suffix - Zielpfad, muss mit '/' beginnen
 * @returns {string}
 */
export const buildPublicPath = (pathname, suffix) => {
  const normalizedSuffix = suffix.startsWith('/') ? suffix : `/${suffix}`;
  if (isHostnameMatched()) {
    return normalizedSuffix;
  }
  const segments = normalizePath(pathname).split('/').filter(Boolean);
  const slug = segments[0] || DEFAULT_TENANT_SLUG;
  return `/${slug}${normalizedSuffix}`;
};
